import React, { useState } from 'react';
import { CalendarDays, List, Check, X, Clock, DollarSign, RotateCcw, Upload, Loader2 } from 'lucide-react';
import { uploadMedia, errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-700', accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600', reschedule_proposed: 'bg-blue-100 text-blue-700',
};
const STATUS_LABEL = { pending: 'Pending', accepted: 'Accepted', rejected: 'Declined', reschedule_proposed: 'Time proposed', pending_payment: 'Awaiting payment' };
const dt = (s) => (s ? String(s).replace('T', ' · ') : '—');
const dayOf = (s) => (s ? String(s).slice(0, 10) : null);

const Actions = ({ r, isAdmin, onAccept, onReject, onPropose, onPayout, onRefund }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const active = ['pending', 'reschedule_proposed'].includes(r.status);
  const propose = () => {
    const val = window.prompt('Propose a new date & time (YYYY-MM-DDTHH:MM):', r.preferred_datetime || '');
    if (val) onPropose(r.id, val);
  };
  const uploadProof = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadMedia(file); onPayout(r.id, url); }
    catch (err) { toast({ title: 'Upload failed', description: errText(err) }); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {active && <button onClick={() => onAccept(r.id)} className="pill-btn text-xs py-1.5" data-testid={`accept-${r.id}`}><Check className="w-3.5 h-3.5" /> Accept</button>}
      {active && <button onClick={() => onReject(r.id)} className="pill-btn-outline text-xs py-1.5" data-testid={`reject-${r.id}`}><X className="w-3.5 h-3.5" /> Decline</button>}
      {active && <button onClick={propose} className="pill-btn-outline text-xs py-1.5" data-testid={`propose-${r.id}`}><Clock className="w-3.5 h-3.5" /> Propose time</button>}
      {isAdmin && !r.is_free && r.payment_status === 'captured' && r.payout_status !== 'paid' && (
        <label className="pill-btn-outline text-xs py-1.5 cursor-pointer" data-testid={`payout-${r.id}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Mark payout paid + proof
          <input type="file" accept="image/*,application/pdf" onChange={uploadProof} className="hidden" />
        </label>
      )}
      {isAdmin && !r.is_free && r.payment_status === 'captured' && r.payout_status !== 'paid' && (
        <button onClick={() => onPayout(r.id, null)} className="pill-btn-outline text-xs py-1.5">Mark paid (no proof)</button>
      )}
      {isAdmin && !r.is_free && r.payment_status === 'captured' && (
        <button onClick={() => onRefund(r.id)} className="text-xs text-red-500 underline py-1.5" data-testid={`refund-${r.id}`}><RotateCcw className="w-3 h-3 inline" /> Refund</button>
      )}
    </div>
  );
};

const Card = ({ r, isAdmin, ...h }) => (
  <div className="rounded-2xl bg-white border border-rose/30 p-4" data-testid={`consult-${r.id}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <div className="font-medium text-burgundy">{r.name} <span className="text-xs text-mahogany/50">· {r.purpose === 'meditation_class' ? 'Meditation class' : 'Consultation'}</span></div>
        <div className="text-xs text-mahogany/60">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
        {isAdmin && <div className="text-xs text-mahogany/50 mt-0.5">for {r.teacher_name}</div>}
      </div>
      <span className={`text-[11px] px-2.5 py-1 rounded-full ${STATUS_STYLE[r.status] || 'bg-mahogany/10 text-mahogany'}`}>{STATUS_LABEL[r.status] || r.status}</span>
    </div>
    <div className="mt-2 text-sm text-mahogany/80 flex flex-wrap gap-x-5 gap-y-1">
      <span><Clock className="w-3.5 h-3.5 inline text-rose-italic" /> {dt(r.proposed_datetime || r.preferred_datetime)}{r.proposed_datetime ? ' (proposed)' : ''}</span>
      <span><DollarSign className="w-3.5 h-3.5 inline text-rose-italic" />
        {r.is_free ? ' Volunteer' : isAdmin
          ? ` ${(r.currency || 'usd').toUpperCase()} ${Number(r.price || 0).toFixed(2)} · payout ${Number(r.payout || 0).toFixed(2)} · ${r.payment_status}${r.payout_status !== 'na' ? ` · payout ${r.payout_status}` : ''}`
          : ` You receive ${(r.currency || 'usd').toUpperCase()} ${Number(r.payout || 0).toFixed(2)}${r.payout_status === 'paid' ? ' · paid' : ''}`}
      </span>
    </div>
    {r.message && <p className="mt-1.5 text-sm text-mahogany/70">{r.message}</p>}
    {r.payout_proof_url && isAdmin && <a href={r.payout_proof_url} target="_blank" rel="noreferrer" className="text-xs text-rose-italic underline">View payout proof</a>}
    <Actions r={r} isAdmin={isAdmin} {...h} />
  </div>
);

const MiniCalendar = (props) => {
  const { items } = props;
  const [ref, setRef] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const first = new Date(ref.y, ref.m, 1);
  const startDow = first.getDay();
  const days = new Date(ref.y, ref.m + 1, 0).getDate();
  const byDay = {};
  items.forEach((r) => { const d = dayOf(r.proposed_datetime || r.preferred_datetime); if (d) (byDay[d] = byDay[d] || []).push(r); });
  const monthName = first.toLocaleString('default', { month: 'long', year: 'numeric' });
  const pad = (n) => String(n).padStart(2, '0');
  const [sel, setSel] = useState(null);
  const move = (delta) => setRef((p) => { const nm = p.m + delta; return { y: p.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => move(-1)} className="pill-btn-outline text-xs py-1">‹ Prev</button>
        <div className="font-serif-display text-lg text-burgundy">{monthName}</div>
        <button onClick={() => move(1)} className="pill-btn-outline text-xs py-1">Next ›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-mahogany/50 mb-1">{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startDow }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: days }).map((_, i) => {
          const key = `${ref.y}-${pad(ref.m + 1)}-${pad(i + 1)}`;
          const list = byDay[key] || [];
          return (
            <button key={key} onClick={() => setSel(list.length ? key : null)}
              className={`aspect-square rounded-lg text-sm flex flex-col items-center justify-center border ${list.length ? 'bg-rose-soft/50 border-rose text-burgundy' : 'border-transparent text-mahogany/70'} ${sel === key ? 'ring-2 ring-mahogany' : ''}`}>
              {i + 1}{list.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-mahogany mt-0.5" />}
            </button>
          );
        })}
      </div>
      {sel && (byDay[sel] || []).length > 0 && (
        <div className="mt-4 space-y-3">{byDay[sel].map((r) => <Card key={r.id} r={r} {...props} />)}</div>
      )}
    </div>
  );
};

const ConsultationBoard = ({ items, isAdmin = false, ...handlers }) => {
  const [view, setView] = useState('list');
  return (
    <div data-testid="consultation-board">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setView('list')} className={view === 'list' ? 'pill-btn text-sm py-1.5' : 'pill-btn-outline text-sm py-1.5'} data-testid="view-list"><List className="w-4 h-4" /> List</button>
        <button onClick={() => setView('calendar')} className={view === 'calendar' ? 'pill-btn text-sm py-1.5' : 'pill-btn-outline text-sm py-1.5'} data-testid="view-calendar"><CalendarDays className="w-4 h-4" /> Calendar</button>
      </div>
      {!items.length ? <p className="text-mahogany/60">No requests yet.</p>
        : view === 'list' ? <div className="space-y-3">{items.map((r) => <Card key={r.id} r={r} isAdmin={isAdmin} {...handlers} />)}</div>
          : <MiniCalendar items={items} isAdmin={isAdmin} {...handlers} />}
    </div>
  );
};

export default ConsultationBoard;
